import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Avatar, Button, CircularProgress, useTheme, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { chatTheme } from '../../theme/chatTheme';
import { getAvatarSrc as getAvatarSrcUtil, DEFAULT_AVATAR } from '../../utils/avatarConstants';
import api from '../../utils/api';

/**
 * AIChatMessage - Displays a single message in the AI chat
 * 
 * @param {Object} props
 * @param {Object} props.message - Message object with sender, content, timestamp
 * @param {boolean} props.isOwnMessage - Whether this is the current user's message
 * @param {string} props.userType - 'couple' or 'supplier'
 * @param {Object} props.user - Current user object with profilePicture
 * @param {Object} props.selectedBusiness - Selected business (for supplier profile image)
 */
const AIChatMessage = ({ message, isOwnMessage, user, userType, selectedBusiness }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isAIMessage = message.senderType === 'ai';
  const isSent = isOwnMessage;
  const [supplierMentions, setSupplierMentions] = useState([]);
  const [loadingSupplier, setLoadingSupplier] = useState(null);
  const [_crmLinks, setCrmLinks] = useState([]);

  // Get user avatar source (for suppliers, prefer business logo)
  const getUserAvatarSrc = () => {
    if (userType === 'supplier' && selectedBusiness?.business?.media?.logo) {
      return getAvatarSrcUtil(selectedBusiness.business.media.logo);
    }
    if (user?.profilePicture) {
      return getAvatarSrcUtil(user.profilePicture);
    }
    return DEFAULT_AVATAR;
  };

  // Get user avatar fallback
  const getUserAvatarFallback = () => {
    if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    }
    if (user?.name) {
      return user.name[0].toUpperCase();
    }
    return 'U';
  };

  // Parse markdown and extract supplier mentions and CRM links
  const parseMessageContent = useCallback((content) => {
    if (!content || !isAIMessage) {
      return { parsedContent: content, supplierNames: [], crmLinks: [] };
    }

    // Extract markdown links like [View Name Profile](/supplier/crm/leads/123)
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const crmLinksFound = [];
    let linkMatch;
    
    while ((linkMatch = markdownLinkRegex.exec(content)) !== null) {
      const linkText = linkMatch[1].trim();
      const linkUrl = linkMatch[2].trim();
      
      // Check if it's a CRM lead link
      if (linkUrl.startsWith('/supplier/crm/leads/')) {
        // Normalize link text: "here", "check here", etc. -> "More Information"
        const displayText = /^(here|check here|click here|see here|view here|more details?)$/i.test(linkText.trim())
          ? 'More Information'
          : linkText;
        crmLinksFound.push({
          text: displayText,
          url: linkUrl
        });
      }
    }

    // Extract supplier names wrapped in ** (but exclude if they're in CRM links or lead context)
    const supplierNameRegex = /\*\*([^*]+)\*\*/g;
    const supplierNames = [];
    let match;
    
    // Appointment / service-type headings often bolded in lists — not supplier business names
    const notSupplierProfileNamePatterns = [
      /^venue viewing$/i,
      /^site visit$/i,
      /^show ?round$/i,
      /^viewing$/i,
      /^consultation$/i,
      /^discovery call$/i,
      /^initial (meeting|consultation)$/i,
      /^follow[- ]?up( meeting| call)?$/i,
      /^follow[- ]?up$/i,
      /^meeting$/i,
      /^phone call$/i,
      /^video call$/i,
      /^in[- ]?person( meeting)?$/i,
      /^trial( session| fitting)?$/i,
      /^fitting$/i,
      /^menu tasting$/i,
      /^cake tasting$/i,
      /^wedding rehearsal$/i,
      /^hair trial$/i,
      /^make[- ]?up trial$/i,
    ];

    // Common label patterns that shouldn't be treated as supplier names
    const commonLabelPatterns = [
      /^Description:?$/i,
      /^Price Range:?$/i,
      /^Location:?$/i,
      /^Category:?$/i,
      /^Email:?$/i,
      /^Phone:?$/i,
      /^Website:?$/i,
      /^Address:?$/i,
      /^Contact:?$/i,
      /^Rating:?$/i,
      /^Reviews?:?$/i,
      /^Lead Name:?$/i,
      /^Wedding Name:?$/i,
      /^Stage:?$/i,
      /^Priority:?$/i,
      // Date/time patterns from chat citations (e.g. "on 13 February at 19:56")
      /^\d{1,2}:\d{2}$/,                    // 19:56
      /^\d{1,2}\s+(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)\.?$/i,
      /^(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|oct|nov|dec)\.?\s+\d{1,2}$/i,
      /^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}$/, // 13/02/2025
      /^\d+$/,                               // plain number
    ];
    
    // Check if content contains lead-related context
    const hasLeadContext = /lead name|stage:|priority:|wedding code|wedding name|partner|partner one|partner two|is partner/i.test(content);
    
    // If there are CRM links, extract names from them to exclude from supplier mentions
    const namesInCrmLinks = new Set();
    crmLinksFound.forEach(link => {
      // Extract potential names from link text (e.g., "View JOhn Doe Profile" -> "JOhn Doe")
      const linkText = link.text;
      // Try to extract name from patterns like "View {Name} Profile" or just "{Name}"
      const nameMatch = linkText.match(/view\s+(.+?)\s+profile/i) || 
                       linkText.match(/^(.+?)\s+profile$/i) ||
                       linkText.match(/(.+)/);
      if (nameMatch && nameMatch[1]) {
        const extractedName = nameMatch[1].trim();
        namesInCrmLinks.add(extractedName.toLowerCase());
        // Also add individual words in case the name is split
        extractedName.split(/\s+/).forEach(word => {
          if (word.length > 2) namesInCrmLinks.add(word.toLowerCase());
        });
      }
    });
    
    while ((match = supplierNameRegex.exec(content)) !== null) {
      const supplierName = match[1].trim();
      const supplierNameLower = supplierName.toLowerCase();
      
      // Skip if it's a common label (with or without colon)
      const isCommonLabel = commonLabelPatterns.some(pattern => pattern.test(supplierName));
      
      // Skip if it ends with a colon (likely a label)
      const isLabel = supplierName.endsWith(':');
      
      // Skip if CRM links exist - don't create supplier buttons when there are CRM links
      // This prevents treating lead names as supplier names
      if (crmLinksFound.length > 0) {
        // Check if this name appears in any CRM link text
        const isInCrmLink = namesInCrmLinks.has(supplierNameLower) || 
          Array.from(namesInCrmLinks).some(name => {
            return supplierNameLower.includes(name) || name.includes(supplierNameLower);
          });
        
        // Also check if name appears after "Lead Name:" label
        const appearsAfterLeadLabel = hasLeadContext && 
          new RegExp(`lead name:?\\s*\\*\\*${supplierName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*`, 'i').test(content);
        
        // If there are CRM links, be very conservative - skip if it could be a lead
        if (isInCrmLink || appearsAfterLeadLabel || hasLeadContext) {
          continue; // Skip this name
        }
      }
      
      const looksLikeAppointmentOrServiceType = notSupplierProfileNamePatterns.some((p) =>
        p.test(supplierName.trim())
      );

      // Skip if it's too short or is a common label
      if (
        supplierName &&
        supplierName.length > 2 &&
        !isCommonLabel &&
        !isLabel &&
        !looksLikeAppointmentOrServiceType
      ) {
        supplierNames.push(supplierName);
      }
    }

    // If there are CRM links or lead context, don't create supplier mention buttons
    // This prevents treating lead names as supplier names
    // Suppliers: only show explicit CRM markdown links as buttons — not **bold** marketplace profile guesses
    const finalSupplierNames =
      userType === 'supplier' || crmLinksFound.length > 0 || hasLeadContext
        ? []
        : [...new Set(supplierNames)];

    return { 
      parsedContent: content, 
      supplierNames: finalSupplierNames,
      crmLinks: crmLinksFound
    };
  }, [isAIMessage, userType]);

  // Sanitize AI response: replace "lead" with "couple" in supplier CRM context
  const sanitizeForSupplierDisplay = (text) => {
    if (!text || !isAIMessage || userType !== 'supplier') return text;
    return text
      .replace(/\blead profile\b/gi, 'couple profile')
      .replace(/\btheir lead\b/gi, 'this couple')
      .replace(/\byour leads\b/gi, 'your couples')
      .replace(/\bthe lead\b/gi, 'the couple')
      .replace(/\byour lead\b/gi, 'your couple');
  };

  const handleInlineLinkClick = (event, url) => {
    event.preventDefault();
    if (!url) return;
    if (url.startsWith('/')) {
      navigate(url);
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Render markdown content with bold text and inline links
  const renderMarkdownContent = (content) => {
    if (!content) return null;

    // Sanitize supplier terminology (lead -> couple)
    let processedContent = sanitizeForSupplierDisplay(content);

    const tokens = [];
    const markdownTokenRegex = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*)/g;
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = markdownTokenRegex.exec(processedContent)) !== null) {
      const fullMatch = match[0];
      const start = match.index;

      if (start > lastIndex) {
        tokens.push(<span key={`text-${key++}`}>{processedContent.slice(lastIndex, start)}</span>);
      }

      // Markdown link token: [text](url)
      if (match[2] && match[3]) {
        const text = match[2];
        const url = match[3];
        tokens.push(
          <Link
            key={`link-${key++}`}
            href={url}
            underline="always"
            onClick={(event) => handleInlineLinkClick(event, url)}
            sx={{
              cursor: 'pointer',
              fontSize: 'inherit',
              lineHeight: 'inherit',
              verticalAlign: 'baseline',
            }}
          >
            {text}
          </Link>
        );
      } else if (match[4]) {
        // Bold token: **text**
        tokens.push(
          <Typography
            key={`bold-${key++}`}
            component="span"
            sx={{
              fontWeight: 600,
              display: 'inline',
              fontSize: 'inherit',
              lineHeight: 'inherit',
            }}
          >
            {match[4]}
          </Typography>
        );
      } else {
        tokens.push(<span key={`raw-${key++}`}>{fullMatch}</span>);
      }

      lastIndex = start + fullMatch.length;
    }

    if (lastIndex < processedContent.length) {
      tokens.push(<span key={`tail-${key++}`}>{processedContent.slice(lastIndex)}</span>);
    }

    return tokens;
  };

  // Search for supplier and navigate to profile
  const handleViewProfile = async (supplierName) => {
    try {
      setLoadingSupplier(supplierName);
      
      // First, try exact name match
      const exactMatchResponse = await api.get('/api/supplier/search', {
        params: { search: supplierName, limit: 20 } // Get more results to find exact match
      });
      
      if (exactMatchResponse.data.suppliers && exactMatchResponse.data.suppliers.length > 0) {
        const normalizedSearchName = supplierName.toLowerCase().trim();
        
        // Try to find exact match first (case-insensitive)
        // Check both businessName and name fields
        const exactMatch = exactMatchResponse.data.suppliers.find(s => {
          const name = (s.businessName || s.name || '').toLowerCase().trim();
          return name === normalizedSearchName;
        });
        
        if (exactMatch) {
          // Found exact match, navigate directly to profile
          navigate(`/dashboard/supplier/${exactMatch._id}`);
          return;
        }
        
        // If no exact match, try partial match (name contains the search term)
        const partialMatch = exactMatchResponse.data.suppliers.find(s => {
          const name = (s.businessName || s.name || '').toLowerCase();
          return name.includes(normalizedSearchName);
        });
        
        if (partialMatch) {
          navigate(`/dashboard/supplier/${partialMatch._id}`);
          return;
        }
        
        // If still no match, use the first result (best match from search)
        const supplier = exactMatchResponse.data.suppliers[0];
        navigate(`/dashboard/supplier/${supplier._id}`);
      } else {
        // If not found, navigate to search page with the supplier name
        navigate(`/supplier-search?search=${encodeURIComponent(supplierName)}`);
      }
    } catch (error) {
      // Fallback to search page
      navigate(`/supplier-search?search=${encodeURIComponent(supplierName)}`);
    } finally {
      setLoadingSupplier(null);
    }
  };

  // Extract supplier mentions and CRM links when message content changes
  useEffect(() => {
    if (isAIMessage && message.content) {
      const { supplierNames, crmLinks: links } = parseMessageContent(message.content);
      setSupplierMentions(supplierNames);
      setCrmLinks(links);
    } else {
      setSupplierMentions([]);
      setCrmLinks([]);
    }
  }, [message.content, isAIMessage, parseMessageContent]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: isSent ? 'flex-end' : 'flex-start',
        mb: 2,
        gap: 1,
      }}
    >
      {/* Avatar - only show for received messages (AI) */}
      {!isSent && (
        <Avatar
          src={isAIMessage ? '/img/lumi.png' : getUserAvatarSrc()}
          sx={{
            width: 32,
            height: 32,
            bgcolor: '#ffffff',
            color: theme.palette.text.primary,
            border: '1px solid rgba(0, 0, 0, 0.1)',
            flexShrink: 0,
            ...(isAIMessage && {
              padding: '5px',
              '& img': {
                objectFit: 'contain',
              },
            }),
          }}
        >
          {!isAIMessage && !getUserAvatarSrc() && getUserAvatarFallback()}
        </Avatar>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSent ? 'flex-end' : 'flex-start',
          maxWidth: { xs: '85%', sm: '75%', md: '70%' },
          minWidth: '120px',
        }}
      >
        {/* Message bubble */}
        <Box
          sx={{
            ...(isSent ? chatTheme.messageBubble.sent : isAIMessage ? chatTheme.messageBubble.ai : chatTheme.messageBubble.received),
            position: 'relative',
            maxWidth: '100%',
            alignSelf: 'unset',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: isSent ? '#fff' : '#2C3E50',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              mb: 0.5,
            }}
          >
            {renderMarkdownContent(message.content)}
          </Typography>
          
          {/* View Profile buttons for mentioned suppliers */}
          {isAIMessage && supplierMentions.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {supplierMentions.map((supplierName, index) => {
                const isLoading = loadingSupplier === supplierName;
                return (
                  <Button
                    key={index}
                    variant="outlined"
                    size="small"
                    onClick={() => handleViewProfile(supplierName)}
                    disabled={loadingSupplier !== null}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.5,
                      borderColor: '#ef4192',
                      color: '#ef4192',
                      '&:hover': {
                        borderColor: '#ef4192',
                        backgroundColor: 'rgba(239, 65, 146, 0.08)',
                      },
                    }}
                  >
                    {isLoading ? (
                      <>
                        <CircularProgress size={12} sx={{ mr: 0.5 }} />
                        Loading...
                      </>
                    ) : (
                      `View ${supplierName} Profile`
                    )}
                  </Button>
                );
              })}
            </Box>
          )}
          
          {/* Timestamp */}
          {message.timestamp && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isSent ? 'rgba(255, 255, 255, 0.7)' : '#9e9e9e',
                  fontSize: '0.7rem',
                }}
              >
                {formatDistanceToNow(new Date(message.timestamp), { addSuffix: true })}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Avatar for sent messages (user) */}
      {isSent && (
        <Avatar
          src={getUserAvatarSrc()}
          sx={{
            width: 32,
            height: 32,
            bgcolor: '#ffffff',
            color: theme.palette.text.primary,
            border: '1px solid rgba(0, 0, 0, 0.1)',
            flexShrink: 0,
          }}
        >
          {!getUserAvatarSrc() && getUserAvatarFallback()}
        </Avatar>
      )}
    </Box>
  );
};

export default AIChatMessage;

